/**
 * RegionsLayer — Sprint 4.2C.
 *
 * Renderiza fleet_regions como L.GeoJSON polygons no mapa.
 * Click em polígono → callback onRegionClick(region) abre RegionDetailModal.
 *
 * Hook lifecycle:
 *   - cria L.FeatureGroup uma vez
 *   - atualiza polygons quando regions mudam
 *   - remove no unmount
 */
import { useEffect, useRef } from "react";

/**
 * @param {object} L                       window.L
 * @param {object} mapRef                  useRef do map
 * @param {Array}  regions                 lista de FleetRegionOut
 * @param {bool}   visible                 mostrar / ocultar
 * @param {function} onRegionClick(region) handler
 */
export function useRegionsLayer({ L, mapRef, regions, visible, onRegionClick }) {
  const groupRef = useRef(null);

  useEffect(() => {
    const map = mapRef?.current;
    if (!map || !L) return;
    if (!groupRef.current) groupRef.current = L.featureGroup().addTo(map);

    const group = groupRef.current;
    group.clearLayers();

    if (!visible || !regions || regions.length === 0) return;

    for (const r of regions) {
      if (!r.geometry_geojson) continue;
      const color = r.color || "#2BD9F0";
      try {
        const layer = L.geoJSON(r.geometry_geojson, {
          style: {
            color,
            weight: 2,
            opacity: 0.9,
            fillColor: color,
            fillOpacity: 0.15,
          },
        });

        // Tooltip permanente com nome (no centro do bounds)
        const bounds = layer.getBounds();
        if (bounds && bounds.isValid && bounds.isValid()) {
          const center = bounds.getCenter();
          const label = L.tooltip({
            permanent: true,
            direction: "center",
            className: "ipwd-region-label",
            opacity: 1,
          }).setLatLng(center).setContent(
            `${r.name}<br/><span class="ipwd-region-label-type">${r.type}</span>`,
          );
          label.addTo(group);
        }

        if (onRegionClick) {
          layer.on("click", () => onRegionClick(r));
        }

        // Hover effect
        layer.on("mouseover", (e) => {
          e.target.setStyle({ fillOpacity: 0.32, weight: 3 });
        });
        layer.on("mouseout", (e) => {
          e.target.setStyle({ fillOpacity: 0.15, weight: 2 });
        });

        layer.addTo(group);
      } catch (err) {
        console.warn(`[RegionsLayer] Falha em ${r.name}:`, err);
      }
    }

    return undefined;
  }, [L, mapRef, regions, visible, onRegionClick]);

  // Cleanup no unmount
  useEffect(() => {
    return () => {
      const map = mapRef?.current;
      if (map && groupRef.current) {
        map.removeLayer(groupRef.current);
        groupRef.current = null;
      }
    };
  }, [mapRef]);
}
